import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { SnackBarService } from '@carelinelive/ui';
import { inject } from '@angular/core';

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        CareLineLive: any;
    }
}

export enum Feature {
    Forms = 'forms',
    NhsOdsOrganisationsSync = 'nhs-ods-organisations-sync',
    PrivacyOfficer = 'privacy-officer',
    Tasks = 'tasks',
    TravelTimeEstimates = 'travel',
    Mfa = 'mfa',
    ClientChoices = 'client-choices',
    GpConnect = 'nhs-gp-connect',

    // Allows users to manage API authentication options (such as creating Personal Access Tokens)
    ApiExternal = 'api-external',
    ClientCarePlan = 'client-care-plan',
    ClientContingencyPlan = 'client-contingency-plan',
    ClientAdvanceStatement = 'client-advance-statement',
    ClientAdditionalSupportPlan = 'client-additional-support-plan',
    ClientFormulation = 'client-formulation',
    WebPush = 'web-push',
    BulkUpdateTool = 'bulk-update-tool',
    SingleSignOn = 'sso',

    McCarthyStone = 'mccarthy-stone',
    EssexCares = 'essex-cares',
    Mcare = 'mcare',
    Prn = 'prn',
    CarerConcernBodyMaps = 'carer-concern-body-maps',

    // Scoped to the PRN / body map batch so the dashboard prompt can be reused for a later one.
    AppConfigPromptsPrnBodyMaps = 'app-config-prompts-prn-body-maps',
    RbacRoleCreation = 'rbac-role-creation',

    CommandPalette = 'command-palette',
    InteractiveMarChart = 'interactive-mar-chart',
    ClientAudit = 'client-audit',
    AsyncBookingCreation = 'async-booking-creation',
    Looker = 'looker',
    ReportsBeta = 'reports-beta',
    HscniSouthernTrust = 'hscni-southern-trust',
    AnnualLeaveRequests = 'annual-leave-requests',
    MessagingHub = 'messaging-hub',
    TotalMobile = 'totalmobile',
    MobileForms = 'mobile-forms',
    Moa = 'moa',
    RosterFilters = 'roster-filters',
    CarerTimesheets = 'carer-timesheets',
}

const featureDefaults: Record<Feature, boolean> = {
    [Feature.Forms]: true,
    [Feature.Mfa]: false,
    [Feature.NhsOdsOrganisationsSync]: false,
    [Feature.PrivacyOfficer]: false,
    [Feature.Tasks]: true,
    [Feature.TravelTimeEstimates]: false,
    [Feature.ClientChoices]: false,
    [Feature.GpConnect]: false,
    [Feature.ApiExternal]: false,
    [Feature.ClientContingencyPlan]: false,
    [Feature.ClientAdvanceStatement]: false,
    [Feature.ClientCarePlan]: false,
    [Feature.ClientAdditionalSupportPlan]: false,
    [Feature.ClientFormulation]: false,
    [Feature.WebPush]: false,
    [Feature.BulkUpdateTool]: false,
    [Feature.SingleSignOn]: false,
    [Feature.McCarthyStone]: false,
    [Feature.RbacRoleCreation]: false,
    [Feature.CommandPalette]: false,
    [Feature.EssexCares]: false,
    [Feature.Mcare]: false,
    [Feature.InteractiveMarChart]: false,
    [Feature.Prn]: false,
    [Feature.CarerConcernBodyMaps]: false,
    [Feature.AppConfigPromptsPrnBodyMaps]: true,
    [Feature.ClientAudit]: false,
    [Feature.AsyncBookingCreation]: false,
    [Feature.Looker]: false,
    [Feature.ReportsBeta]: false,
    [Feature.HscniSouthernTrust]: false,
    [Feature.MessagingHub]: false,
    [Feature.MobileForms]: false,
    [Feature.TotalMobile]: false,
    [Feature.AnnualLeaveRequests]: false,
    [Feature.Moa]: false,
    [Feature.RosterFilters]: false,
    [Feature.CarerTimesheets]: false,
};

const features = {
    ...window.CareLineLive.features,
};

export const travelEnabled = isFeatureEnabled(Feature.TravelTimeEstimates);
export const mfaEnabled = isFeatureEnabled(Feature.Mfa);

export function isFeatureEnabled(feature: Feature): boolean {
    if (features[feature] === true) {
        return true;
    }

    return featureDefaults[feature];
}

export function guardFeature(feature: Feature): CanActivateFn {
    return () => {
        if (isFeatureEnabled(feature)) {
            return true;
        }

        inject(SnackBarService).openFlag({
            icon: 'error',
            colour: 'error',
            title: `Feature "${feature}" is disabled on this environment`,
            message: 'Contact support if you think it should be enabled',
        });

        return inject(Router).createUrlTree(['/']);
    };
}

const integrations = window.CareLineLive.config?.integrations;

export function isXeroIntegrationEnabled() {
    return integrations?.xero?.enabled === true;
}

export function isSageIntegrationEnabled() {
    return integrations?.sage?.enabled === true;
}

export function isBhccEnabled() {
    return integrations?.bhcc?.enabled === true;
}

export function isHertfordshireEnabled() {
    return integrations?.hertfordshire?.enabled === true;
}

export function isCM2000IntegrationEnabled() {
    return integrations?.cm2000?.enabled === true;
}

export function isSheffieldCityCouncilEnabled() {
    return integrations?.sheffield_city_council?.enabled === true;
}

export function isKccMosaicEnabled(): boolean {
    return integrations?.kcc_mosaic?.enabled === true;
}

export const isOffshorePayrollEnabled = integrations?.offshore_payroll?.enabled === true;

export function isIrisIntegrationEnabled() {
    return integrations?.iris?.enabled === true;
}
